import { RefObject, useCallback } from 'react';

import { EntityDocumentViewer_FilePageFragment } from '@/modules/documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import { DocumentDetailsViewOptions } from '@/modules/documents/documents.constants';
import { useCreateSummary } from '@/modules/documents/hooks/useCreateSummary';
import { SelectableDocument } from '@/modules/documents/ManageDocumentAmendments/types';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import type { PDFSource } from '@/modules/pdf/PDFViewer';
import { DocumentType } from '@/types/schema';

import { useDocumentDetails } from '../../contexts/DocumentDetails.context';
import { SummaryLoadingState } from '../../DocumentDetailsPage.types';
import { DocumentViewer } from './DocumentViewer';

interface DocumentViewerContainerProps {
  documentId: string;
  source: PDFSource | null;
  documentFilePages: EntityDocumentViewer_FilePageFragment[];
  documentViewerHeight: string;
  aiEnabled: boolean;
  activeTab: DocumentDetailsViewOptions;
  setActiveTab: (tab: DocumentDetailsViewOptions) => void;
  viewerRef: RefObject<HTMLDivElement | null>;
  summaryLoadingState: SummaryLoadingState;
  onGenerateAiSummary: () => void;
  documentType?: DocumentType;
  associatedDocuments?: SelectableDocument[];
  onOpenChatWithSummary?: () => void;
}

function DocumentViewerContainerInner(props: DocumentViewerContainerProps) {
  const { householdId } = useHouseholdDetailsContext();
  const { documentId, onGenerateAiSummary, onOpenChatWithSummary } = props;
  const { openEditSummaryTrowser, setSelectedSummaryId } = useDocumentDetails();
  const { createSummary } = useCreateSummary({ documentId });

  const handleWriteSummary = useCallback(async () => {
    if (!householdId) return;
    const result = await createSummary({
      variables: {
        documentID: documentId,
        input: {
          documentv2ID: documentId,
          householdID: householdId,
          summary: '',
        },
      },
    });

    if (result?.data?.createDocumentSummary?.id) {
      setSelectedSummaryId(result.data.createDocumentSummary.id);
      openEditSummaryTrowser();
    }
  }, [
    createSummary,
    documentId,
    householdId,
    openEditSummaryTrowser,
    setSelectedSummaryId,
  ]);

  return (
    <DocumentViewer
      {...props}
      onWriteSummary={handleWriteSummary}
      onGenerateAiSummary={onGenerateAiSummary}
      onOpenChatWithSummary={onOpenChatWithSummary}
    />
  );
}

export function DocumentViewerContainer(props: DocumentViewerContainerProps) {
  return <DocumentViewerContainerInner {...props} />;
}
